// src/components/directory/ProgramModal.jsx
// Covers: PL-02 (full detail), PL-03 (eligibility), GV-03 (flag a listing)

import React, { useEffect, useCallback } from 'react';
import { flagProgram } from '../../hooks/usePrograms';
import styles from './ProgramModal.module.css';

const CAT_LABELS = {
  mental:'Mental health', financial:'Financial', housing:'Housing',
  employment:'Employment', family:'Family', medical:'Medical & rehab', indigenous:'Indigenous',
};

export default function ProgramModal({ program, onClose }) {
  const {
    title, org, url, phone, category,
    cost, referral, delivery, geo, familyOK, rural,
    eligWho = [], eligNot = [],
    desc, verified, verifiedDate, verifiedBy,
    tags = [], indigenous,
  } = program;

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleFlag = useCallback(async () => {
    const reason = window.prompt('What\'s wrong with this listing? (e.g. outdated info, wrong phone number, program no longer exists)');
    if (!reason?.trim()) return;
    await flagProgram({
      programId:    program.id,
      programTitle: title,
      reason:       reason.trim(),
    });
    alert('Thank you — we\'ll review this listing within 5 business days.');
  }, [program.id, title]);

  const costGood     = cost.toLowerCase().includes('free');
  const referralGood = referral.toLowerCase().includes('no referral');

  return (
    <div className={styles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label={title}>

        {/* Header */}
        <div className={styles.header}>
          <div>
            <div className={styles.headerMeta}>
              <span className={`${styles.catPill} ${styles[`cat_${category}`]}`}>
                {CAT_LABELS[category] || category}
              </span>
              <span className={`${styles.verifyBadge} ${verified ? styles.verified : styles.pending}`}>
                <span className={styles.verifyDot} />
                {verified
                  ? `Verified — last reviewed ${formatDate(verifiedDate)} by ${verifiedBy}`
                  : 'Pending review — information may be incomplete'}
              </span>
            </div>
            <h2 className={styles.title}>{title}</h2>
            <p className={styles.org}>{org}</p>
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Body */}
        <div className={styles.body}>

          {/* Facts grid */}
          <div className={styles.factsGrid}>
            <ModalFact label="Cost"          value={cost}     good={costGood} />
            <ModalFact label="Referral"      value={referral} good={referralGood} warn={!referralGood} />
            <ModalFact label="Format"        value={delivery} />
            <ModalFact label="Geographic scope" value={geo}  />
            <ModalFact label="Family access" value={familyOK ? 'Yes — without Veteran' : 'Requires Veteran'} good={familyOK} />
            <ModalFact label="Rural/remote"  value={rural ? 'Available' : 'Not specified'} good={rural} />
          </div>

          {/* Eligibility block — PL-03 */}
          <div className={styles.eligBlock}>
            <div className={styles.eligTitle}>Who can access this program</div>
            <div className={styles.eligRows}>
              {eligWho.map((e, i) => (
                <div key={i} className={styles.eligRow}>
                  <span className={styles.check}>✓</span> {e}
                </div>
              ))}
              {eligNot.map((e, i) => (
                <div key={i} className={`${styles.eligRow} ${styles.eligNot}`}>
                  <span className={styles.dot}>·</span> {e}
                </div>
              ))}
            </div>
          </div>

          {/* About */}
          <div className={styles.field}>
            <span className={styles.fieldLabel}>About this program</span>
            <p className={styles.fieldValue}>{desc}</p>
          </div>

          {/* Tags */}
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Tags</span>
            <div className={styles.tags}>
              {tags.map(t => <span key={t} className={styles.tag}>{t}</span>)}
              {rural && <span className={`${styles.tag} ${styles.tagRural}`}>Rural/remote</span>}
              {indigenous && <span className={`${styles.tag} ${styles.tagIndigenous}`}>Indigenous</span>}
            </div>
          </div>

          {/* Contact */}
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Phone</span>
            <a href={`tel:${phone}`} className={styles.phone}>{phone}</a>
          </div>
        </div>

        {/* Footer actions */}
        <div className={styles.footer}>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.btnPrimary}
          >
            Visit program website →
          </a>
          <button className={styles.btnSecondary} onClick={onClose}>
            Close
          </button>
          {/* GV-03: Flag a listing */}
          <button className={styles.btnFlag} onClick={handleFlag}>
            Flag an issue
          </button>
        </div>

      </div>
    </div>
  );
}

function ModalFact({ label, value, good, warn }) {
  return (
    <div className={styles.fact}>
      <div className={styles.factLabel}>{label}</div>
      <div className={`${styles.factValue} ${good ? styles.good : ''} ${warn ? styles.warn : ''}`}>
        {value}
      </div>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });
  } catch { return dateStr; }
}
